<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const props = defineProps({
  fleet: {
    type: Object,
    default: () => ({ available: 0, rented: 0, maintenance: 0, retired: 0, total: 0 })
  }
})

const segments = computed(() => [
  { key: 'available',   label: t('vehicles.status.available'),   tone: 'ok',     value: props.fleet.available   },
  { key: 'rented',      label: t('vehicles.status.rented'),      tone: 'accent', value: props.fleet.rented      },
  { key: 'maintenance', label: t('vehicles.status.maintenance'), tone: 'warn',   value: props.fleet.maintenance },
  { key: 'retired',     label: t('vehicles.status.retired'),     tone: 'muted',  value: props.fleet.retired, dimmed: true },
])

const total = computed(() => props.fleet.total ?? 0)

const R = 70, cx = 85, cy = 85, stroke = 14
const circumference = 2 * Math.PI * R

const arcs = computed(() => {
  const divisor = total.value || 1
  let offset = 0
  return segments.value.map(seg => {
    const pct = seg.value / divisor
    const dash = pct * circumference
    const arc = { ...seg, dash, gap: circumference - dash, offset: -offset }
    offset += dash
    return arc
  })
})
</script>

<template>
  <div class="flex flex-col gap-4 items-center">
    <div class="relative" style="width: 170px; height: 170px;">
      <svg width="170" height="170" viewBox="0 0 170 170" style="transform: rotate(-90deg);" role="img" :aria-label="`Fleet: ${total} vehicles`">
        <circle :cx="cx" :cy="cy" :r="R" fill="none" stroke="var(--surfaceAlt)" :stroke-width="stroke"/>
        <circle
          v-for="arc in arcs"
          :key="arc.key"
          :cx="cx" :cy="cy" :r="R"
          fill="none"
          :stroke="`var(--${arc.tone})`"
          :stroke-width="stroke"
          :stroke-dasharray="`${arc.dash} ${arc.gap}`"
          :stroke-dashoffset="arc.offset"
          :opacity="arc.dimmed ? 0.5 : 1"
          stroke-linecap="butt"
        />
      </svg>
      <div class="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
        <span class="font-display" style="font-size: 2rem; color: var(--ink);">{{ total }}</span>
        <span class="mono text-[0.625rem] uppercase tracking-[0.08em]" style="color: var(--muted);">vehicles</span>
      </div>
    </div>

    <div class="grid grid-cols-2 gap-1.5 w-full">
      <a
        v-for="seg in segments"
        :key="seg.key"
        :href="`/vehicles?status=${seg.key}`"
        class="flex items-center gap-2 text-xs px-1 py-0.5 rounded hover:bg-[var(--surfaceAlt)] transition-colors"
        :class="seg.dimmed ? 'opacity-60' : ''"
        style="color: var(--inkSoft);"
      >
        <span class="w-2 h-2 rounded-[2px] flex-shrink-0" :style="{ backgroundColor: `var(--${seg.tone})` }"></span>
        <span class="flex-1">{{ seg.label }}</span>
        <span class="mono" style="color: var(--muted);">{{ seg.value }}</span>
      </a>
    </div>
  </div>
</template>
